/**
 * Native (Capacitor) integration bridge.
 *
 * Works cleanly in both environments:
 *   - Browser (web): falls back to web APIs / no-ops
 *   - Native (iOS/Android): uses Capacitor plugins
 *
 * Import once from App.js to bootstrap native behavior.
 */
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { PushNotifications } from "@capacitor/push-notifications";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Geolocation } from "@capacitor/geolocation";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { api } from "@/lib/api";

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

/** Called once at app boot. */
export async function bootstrapNative() {
  if (!isNative()) return;

  // Status bar — sage brand color
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: "#3f6b52" });
  } catch (e) {
    console.warn("[native] status bar setup failed", e);
  }

  // Register for push
  try {
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive === "granted") {
      await PushNotifications.register();
    }
    PushNotifications.addListener("registration", async (token) => {
      try {
        await api.post("/notifications/register-device", {
          token: token.value,
          platform: platform(),
        });
      } catch (e) {
        console.warn("[native] device token registration failed", e);
      }
    });
    PushNotifications.addListener("pushNotificationReceived", (n) => {
      console.info("[push] received in foreground", n);
    });
    PushNotifications.addListener("pushNotificationActionPerformed", (act) => {
      const link = act.notification?.data?.link;
      if (link) window.location.href = link;
    });
  } catch (e) {
    console.warn("[native] push setup failed", e);
  }

  // Deep links — open autismbabysitters.com/... URLs directly inside the app
  try {
    CapApp.addListener("appUrlOpen", (event) => {
      try {
        const url = new URL(event.url);
        if (url.pathname) window.location.href = url.pathname + url.search;
      } catch (e) {
        console.warn("[native] deep link parse failed", e);
      }
    });
  } catch (e) {
    console.warn("[native] deep link listener failed", e);
  }

  // Hide splash after web content is mounted
  setTimeout(() => {
    SplashScreen.hide().catch((e) => console.warn("[native] splash hide failed", e));
  }, 400);
}

/**
 * Capture a photo. Returns a data-URL for immediate preview + a File-like Blob.
 * Web fallback: returns null so caller can use its existing <input type=file />.
 */
export async function capturePhoto() {
  if (!isNative()) return null;
  try {
    const image = await Camera.getPhoto({
      quality: 80,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Prompt,
      correctOrientation: true,
    });
    return image.dataUrl || null;
  } catch (e) {
    console.warn("[native] camera cancelled/failed", e);
    return null;
  }
}

/** One-off geolocation lookup. Web fallback uses navigator.geolocation. */
export async function getCurrentPosition() {
  if (isNative()) {
    const perm = await Geolocation.requestPermissions();
    if (perm.location !== "granted") throw new Error("Location permission denied");
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
    return { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
  }
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      reject,
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
}

/** Light haptic tap on native, no-op on web. */
export async function tapHaptic() {
  if (!isNative()) return;
  try { await Haptics.impact({ style: ImpactStyle.Light }); } catch (_e) {}
}
